import { Expense } from '../types';

export interface RecurrenceSeriesMeta {
  seriesId: string;
  endDate?: string;
}

export const parseRecurrenceMeta = (rawJson: unknown): RecurrenceSeriesMeta | null => {
  let parsed = rawJson;
  if (typeof rawJson === 'string') {
    try {
      parsed = JSON.parse(rawJson);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const recurrence = (parsed as { recurrence?: { series_id?: string; end_date?: string } }).recurrence;
  if (!recurrence?.series_id) return null;
  return {
    seriesId: String(recurrence.series_id),
    endDate: recurrence.end_date || undefined,
  };
};

export const getExpenseRecurrenceMeta = (expense: Expense): RecurrenceSeriesMeta | null =>
  parseRecurrenceMeta(expense.originalData?.raw_json_data);

export const toIsoDateOnly = (value?: string): string => {
  if (value && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  return '';
};

export const todayIsoDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isIsoDateInPast = (value?: string): boolean => {
  const iso = toIsoDateOnly(value);
  return Boolean(iso && iso < todayIsoDate());
};

const reminderStorageKey = (tenantId: string) => `recurrenceReminders:${tenantId}`;

export const canShowRecurrenceReminder = (tenantId: string, seriesId: string, maxPerMonth = 3): boolean => {
  try {
    const stored = JSON.parse(localStorage.getItem(reminderStorageKey(tenantId)) || '{}') as Record<
      string,
      Record<string, number>
    >;
    const monthKey = todayIsoDate().slice(0, 7);
    return (stored[seriesId]?.[monthKey] || 0) < maxPerMonth;
  } catch {
    return true;
  }
};

export const markRecurrenceReminderShown = (tenantId: string, seriesId: string): void => {
  try {
    const key = reminderStorageKey(tenantId);
    const stored = JSON.parse(localStorage.getItem(key) || '{}') as Record<string, Record<string, number>>;
    const monthKey = todayIsoDate().slice(0, 7);
    stored[seriesId] = stored[seriesId] || {};
    stored[seriesId][monthKey] = (stored[seriesId][monthKey] || 0) + 1;
    localStorage.setItem(key, JSON.stringify(stored));
  } catch {
    // Ignore storage failures; reminder is best-effort.
  }
};
